import { MCP_STRUCTURED_OUTPUT_KEY } from './mcpConnection';
import {
  ActionType,
  CLASSIFY_ACTIONABILITY_SOURCE_KIND_KEY,
  TriggerType,
} from './userCommunicationAgent';

export const MailboxConfigField = {
  SENDER: 'senderEmailAddress',
  CALENDAR: 'calendarEmailAddress',
} as const;
export type MailboxConfigField =
  (typeof MailboxConfigField)[keyof typeof MailboxConfigField];

export enum StepKind {
  ACTION = 'ACTION',
  CONTAINER = 'CONTAINER',
}

/**
 * What a step leaves under `step.step-N.output` after it succeeds. A failed or
 * skipped step may write '' (or nothing); `kind` describes the success shape.
 */
export enum StepOutputKind {
  NONE = 'NONE',
  SCALAR = 'SCALAR',
  JSON_OBJECT = 'JSON_OBJECT',
  JSON_ARRAY = 'JSON_ARRAY',
  BOOLEAN_STRING = 'BOOLEAN_STRING',
  /** Free text, unless the config carries a valid `outputSchema`: then a JSON object with the schema's properties as keys. */
  SCHEMA_DEFINED = 'SCHEMA_DEFINED',
  /** No bare `.output`; only exact `.output.<key>` context entries. */
  CONTEXT_KEYS = 'CONTEXT_KEYS',
}

export const LEGACY_DECISION_KEY = 'decision';
export const ASK_USER_QUESTION_KEY = 'question';

export const BARE_OUTPUT_LIST = '$output';

export enum ItemFieldType {
  VALUE = 'VALUE',
  LIST = 'LIST',
  OBJECT = 'OBJECT',
  OBJECT_LIST = 'OBJECT_LIST',
}

export interface ItemField {
  key: string;
  type: ItemFieldType;
  /** Absent on the happy path for some items. */
  optional?: boolean;
  /** OBJECT / OBJECT_LIST: the nested object's fields. */
  fields?: readonly ItemField[];
  /** OBJECT / OBJECT_LIST: keys the runtime writes on the nested object but never offers; they resolve without a warning. */
  hiddenFields?: readonly string[];
}

export enum ItemShapeKind {
  OBJECT = 'OBJECT',
  /** The item is the value itself (a list of ids). */
  SCALAR = 'SCALAR',
  /** A list whose items cannot be known ahead of the run. */
  UNKNOWN = 'UNKNOWN',
}

export type ItemShape =
  | {
      kind: ItemShapeKind.OBJECT;
      fields: readonly ItemField[];
      /** Keys the runtime writes on the item but never offers to the user or the generator. */
      hiddenFields?: readonly string[];
    }
  | { kind: ItemShapeKind.SCALAR }
  | { kind: ItemShapeKind.UNKNOWN };

export interface StepOutputContract {
  kind: StepOutputKind;
  /** JSON_OBJECT: the JSON keys. JSON_ARRAY / SCALAR / CONTEXT_KEYS: the exact `.output.<key>` context entries. */
  keys?: readonly string[];
  /** JSON_OBJECT: the key whose value is the list a FOR_EACH loops over. */
  arrayKey?: string;
  /** Subset of `keys` absent on the happy path. */
  optionalKeys?: readonly string[];
  /** Keys the generator's core catalogue names; absent = every public key, [] = the catalogue says "ids" and get_step_spec lists them. */
  briefKeys?: readonly string[];
  /** Subset of `keys` holding JSON the runtime shapes: `.output.<key>.<any path>` resolves and cannot be checked ahead of the run. */
  jsonKeys?: readonly string[];
  /** SCALAR: the closed set of values the step can write. */
  values?: readonly string[];
  /** Other `step.step-N.<key>` entries the step writes beside `.output`. */
  extraContextKeys?: readonly string[];
  /** Subset of `extraContextKeys` not written on the happy path. */
  conditionalContextKeys?: readonly string[];
  /** Subset of `extraContextKeys` written as soon as the step pauses, not on resume. */
  writtenOnPauseKeys?: readonly string[];
  /** `.output.<key>` entries the runtime still writes for flows saved before the key was dropped: accepted by the validator, never offered or suggested. */
  legacyKeys?: readonly string[];
  /**
   * Item shape of each list the step outputs, keyed by `BARE_OUTPUT_LIST` (the bare `.output`), a `keys` entry
   * (`.output.<key>`) or an `extraContextKeys` entry. A list from an `outputSchema` (AI_ASK, AGENT_TASK) is not
   * declared here: the list is whichever schema property the flow names, and its items are that property's `items`.
   */
  itemShapes?: Readonly<Record<string, ItemShape>>;
  /** stepConfig field holding the JSON schema that turns a SCHEMA_DEFINED output structured. */
  outputSchemaField?: string;
  /** The runner, not the strategy, writes `.output` once the paused step resumes. */
  writtenOnResume?: boolean;
}

export interface StepContract {
  kind: StepKind;
  output: StepOutputContract;
  /** stepConfig paths resolved as `{{...}}` templates (`[]` = array element, `*` = every string value). */
  templateFields: readonly string[];
  /** stepConfig paths read as a bare variable path, e.g. `step.step-1.output.id`. */
  barePathFields: readonly string[];
  /** stepConfig paths the runtime never template-resolves: a `{{...}}` there is used as literal text. */
  literalFields?: readonly string[];
  /** stepConfig paths the runtime reads as one string: an array or object there throws at run time. */
  stringOnlyFields?: readonly string[];
  childOutputsAddressable?: boolean;
  /** A child reads an EARLIER sibling's `step.step-N.child-C.output` within the same iteration; unlike `childOutputsAddressable`, nothing resolves outside the loop. */
  childOutputsReadableWithinIteration?: boolean;
  childScopeVariables?: readonly string[];
  runtimeSupported: boolean;
  webSupported: boolean;
  /** The step acts on the triggering email and cannot run without one. */
  requiresTriggerEmail: boolean;
  /** The step suspends the run until the user answers; the runner resumes at the next top-level step. */
  pausesFlow: boolean;
  /** The stepConfig field naming the mailbox the step acts as. */
  mailboxField?: MailboxConfigField;
  /** The step (or its tools) reads the run's trigger message at runtime. Defaults to true: a type opts out only after its strategy path is verified not to touch it. A container's own flag covers its own code; the children are walked by `stepTreeReadsTrigger`. */
  readsTrigger: boolean;
  /** Running the step again after a kill has no external side effect (a pure read or evaluation). Defaults to false. A container's own flag covers its own code; the children are walked by `stepTreeIsIdempotent`. */
  idempotent: boolean;
}

const NO_OUTPUT = { kind: StepOutputKind.NONE } as const;

const jsonObject = <
  const Keys extends readonly string[],
  const Optional extends readonly string[] = readonly [],
>(
  keys: Keys,
  optionalKeys?: Optional,
) =>
  ({
    kind: StepOutputKind.JSON_OBJECT,
    keys,
    ...(optionalKeys && { optionalKeys }),
  }) as {
    kind: StepOutputKind.JSON_OBJECT;
    keys: Keys;
    optionalKeys: Optional;
  };

const withBriefKeys = <
  const Output extends StepOutputContract,
  const Brief extends readonly string[],
>(
  output: Output,
  briefKeys: Brief,
) => ({ ...output, briefKeys });

const withJsonKeys = <
  const Output extends StepOutputContract,
  const Json extends readonly string[],
>(
  output: Output,
  jsonKeys: Json,
) => ({ ...output, jsonKeys });

const listedIn = <const Output extends { keys: readonly string[] }>(
  output: Output,
  arrayKey: Output['keys'][number],
) => ({ ...output, arrayKey });

const jsonArray = <
  const Keys extends readonly string[] = readonly [],
  const Optional extends readonly string[] = readonly [],
>(
  keys?: Keys,
  optionalKeys?: Optional,
) =>
  ({
    kind: StepOutputKind.JSON_ARRAY,
    ...(keys && { keys }),
    ...(optionalKeys && { optionalKeys }),
  }) as {
    kind: StepOutputKind.JSON_ARRAY;
    keys: Keys;
    optionalKeys: Optional;
  };

const itemField = (
  key: string,
  type: ItemFieldType = ItemFieldType.VALUE,
  optional = false,
  fields?: readonly ItemField[],
): ItemField => ({
  key,
  type,
  ...(optional && { optional }),
  ...(fields && { fields }),
});

const requiredField = (key: string) => itemField(key);
const optionalField = (key: string) =>
  itemField(key, ItemFieldType.VALUE, true);
const listField = (key: string, optional = false) =>
  itemField(key, ItemFieldType.LIST, optional);
const objectListField = (
  key: string,
  fields: readonly ItemField[],
  optional = false,
) => itemField(key, ItemFieldType.OBJECT_LIST, optional, fields);

const objectItems = (
  fields: readonly ItemField[],
  hiddenFields?: readonly string[],
): ItemShape => ({
  kind: ItemShapeKind.OBJECT,
  fields,
  ...(hiddenFields && { hiddenFields }),
});

const SCALAR_ITEMS: ItemShape = { kind: ItemShapeKind.SCALAR };
const UNKNOWN_ITEMS: ItemShape = { kind: ItemShapeKind.UNKNOWN };

const withItemShapes = <
  const Output extends StepOutputContract,
  const Shapes extends Readonly<Record<string, ItemShape>>,
>(
  output: Output,
  itemShapes: Shapes,
) => ({ ...output, itemShapes });

const PERSON_FIELDS = [
  requiredField('personId'),
  requiredField('displayName'),
  listField('emails'),
  listField('phones', true),
  optionalField('jobTitle'),
  requiredField('origin'),
] as const;

const COMMUNICATION_FIELDS = [
  requiredField('messageId'),
  optionalField('subject'),
  requiredField('body'),
  requiredField('author'),
  optionalField('authorEmail'),
  optionalField('platform'),
  optionalField('channel'),
  optionalField('channelName'),
  requiredField('createdAt'),
  listField('labelIds', true),
] as const;

const CHAT_MESSAGE_FIELDS = [
  requiredField('messageId'),
  requiredField('platform'),
  requiredField('conversationId'),
  requiredField('conversationKind'),
  requiredField('author'),
  optionalField('authorEmail'),
  optionalField('authorPlatformId'),
  requiredField('body'),
  requiredField('createdAt'),
  requiredField('isFromMe'),
  optionalField('messageUrl'),
  objectListField(
    'attachments',
    [requiredField('name'), optionalField('type'), optionalField('url')],
    true,
  ),
  optionalField('threadId'),
  optionalField('isThreadReply'),
  optionalField('subject'),
  objectListField(
    'mentions',
    [requiredField('name'), requiredField('isMe')],
    true,
  ),
  objectListField(
    'recipients',
    [
      optionalField('name'),
      requiredField('email'),
      optionalField('type'),
      requiredField('isMe'),
    ],
    true,
  ),
] as const;

const CHAT_MESSAGE_HIDDEN_FIELDS = [
  'authorPlatformIdHmac',
  'conversationKey',
  'isContext',
  'signals',
] as const;

const DOBOX_FIELDS = [
  requiredField('id'),
  optionalField('subject'),
  optionalField('description'),
  requiredField('dueAt'),
  requiredField('isSuggestion'),
  requiredField('looksResolved'),
  listField('participantIds'),
  listField('taskLabelIds'),
  requiredField('createdAt'),
  requiredField('updatedAt'),
  requiredField('resolvedAt'),
  requiredField('createdDateTime'),
  requiredField('lastActivityDateTime'),
  requiredField('daysSinceLastActivity'),
  requiredField('sourceMessageId'),
  requiredField('sourceAuthorEmail'),
  requiredField('sourceEmailAddress'),
  requiredField('sourcePlatform'),
  requiredField('lastMessagePreview'),
] as const;

const MEETING_FIELDS = [
  requiredField('eventId'),
  requiredField('summary'),
  requiredField('startDateTime'),
  requiredField('endDateTime'),
  requiredField('timeZone'),
  requiredField('location'),
  requiredField('organizer'),
  listField('attendees'),
  requiredField('description'),
  requiredField('onlineMeetingUrl'),
] as const;

const FACEBOOK_POST_FIELDS = [
  requiredField('postId'),
  optionalField('message'),
  optionalField('story'),
  optionalField('createdAt'),
  optionalField('permalink'),
  optionalField('isPublished'),
  optionalField('scheduledFor'),
] as const;

const FACEBOOK_COMMENT_FIELDS = [
  requiredField('commentId'),
  optionalField('message'),
  optionalField('createdAt'),
  optionalField('authorId'),
  optionalField('authorName'),
  optionalField('likeCount'),
  optionalField('replyCount'),
  optionalField('isHidden'),
  optionalField('parentCommentId'),
] as const;

const TEAM_MEMBER_FIELDS = [
  requiredField('userId'),
  requiredField('name'),
  requiredField('email'),
] as const;

interface ActionFlags {
  runtimeSupported?: boolean;
  webSupported?: boolean;
  requiresTriggerEmail?: boolean;
  pausesFlow?: boolean;
  mailboxField?: MailboxConfigField;
  readsTrigger?: boolean;
  idempotent?: boolean;
}

type FlagValue<
  Flags extends ActionFlags,
  Key extends keyof ActionFlags,
  Default = true,
> = Flags extends Record<Key, infer Value> ? Value : Default;

const action = <
  const Output extends StepOutputContract,
  const Flags extends ActionFlags = Record<never, never>,
>(
  output: Output,
  fields: {
    templateFields?: readonly string[];
    barePathFields?: readonly string[];
    literalFields?: readonly string[];
    stringOnlyFields?: readonly string[];
  } = {},
  flags: Flags = {} as Flags,
): Omit<
  StepContract,
  | 'kind'
  | 'output'
  | 'runtimeSupported'
  | 'webSupported'
  | 'requiresTriggerEmail'
  | 'pausesFlow'
  | 'mailboxField'
  | 'readsTrigger'
  | 'idempotent'
> & {
  kind: StepKind.ACTION;
  output: Output;
  runtimeSupported: FlagValue<Flags, 'runtimeSupported'>;
  webSupported: FlagValue<Flags, 'webSupported'>;
  requiresTriggerEmail: FlagValue<Flags, 'requiresTriggerEmail', false>;
  pausesFlow: FlagValue<Flags, 'pausesFlow', false>;
  mailboxField: FlagValue<Flags, 'mailboxField', undefined>;
  readsTrigger: boolean;
  idempotent: boolean;
} => ({
  kind: StepKind.ACTION,
  output,
  templateFields: fields.templateFields ?? [],
  barePathFields: fields.barePathFields ?? [],
  literalFields: fields.literalFields ?? [],
  stringOnlyFields: fields.stringOnlyFields ?? [],
  runtimeSupported: (flags.runtimeSupported ?? true) as FlagValue<
    Flags,
    'runtimeSupported'
  >,
  webSupported: (flags.webSupported ?? true) as FlagValue<
    Flags,
    'webSupported'
  >,
  requiresTriggerEmail: (flags.requiresTriggerEmail ?? false) as FlagValue<
    Flags,
    'requiresTriggerEmail',
    false
  >,
  pausesFlow: (flags.pausesFlow ?? false) as FlagValue<
    Flags,
    'pausesFlow',
    false
  >,
  mailboxField: flags.mailboxField as FlagValue<
    Flags,
    'mailboxField',
    undefined
  >,
  readsTrigger: flags.readsTrigger ?? true,
  idempotent: flags.idempotent ?? false,
});

export const FOR_EACH_ITEM_VARIABLE = 'forEach.item';
export const FOR_EACH_INDEX_VARIABLE = 'forEach.index';
export const FOR_EACH_TOTAL_VARIABLE = 'forEach.total';

export const FOR_EACH_RESULTS_KEY = 'results';
export const FOR_EACH_RESULT_CHILD_PREFIX = 'child-';
export const FOR_EACH_RESULT_FIELD = {
  INDEX: 'index',
  ITEM: 'item',
  OUTPUTS: 'outputs',
  SKIPPED: 'skipped',
  PARTIAL: 'partial',
  TRUNCATED: 'truncated',
  VALUE: 'value',
} as const;

export interface ForEachResultEntry {
  [FOR_EACH_RESULT_FIELD.INDEX]: number;
  [FOR_EACH_RESULT_FIELD.ITEM]?: unknown;
  [FOR_EACH_RESULT_FIELD.OUTPUTS]?: Record<string, unknown>;
  [FOR_EACH_RESULT_FIELD.SKIPPED]?: true;
  [FOR_EACH_RESULT_FIELD.PARTIAL]?: true;
  [FOR_EACH_RESULT_FIELD.TRUNCATED]?: true;
}

const FOR_EACH_RESULT_ITEM_SHAPES = {
  [FOR_EACH_RESULTS_KEY]: objectItems([
    itemField(FOR_EACH_RESULT_FIELD.INDEX),
    itemField(FOR_EACH_RESULT_FIELD.ITEM),
    itemField(FOR_EACH_RESULT_FIELD.OUTPUTS, ItemFieldType.OBJECT),
    itemField(FOR_EACH_RESULT_FIELD.SKIPPED, ItemFieldType.VALUE, true),
    itemField(FOR_EACH_RESULT_FIELD.PARTIAL, ItemFieldType.VALUE, true),
    itemField(FOR_EACH_RESULT_FIELD.TRUNCATED, ItemFieldType.VALUE, true),
  ]),
};

const CALENDAR_RESULT_KEYS = ['success', 'eventId'] as const;

const DRAFT_OUTPUT = withBriefKeys(
  jsonObject(['draftId', 'threadId', 'accountAddress', 'platform']),
  [],
);
const SEND_EMAIL_OUTPUT = withBriefKeys(
  jsonObject(['messageId', 'threadId'], ['messageId']),
  [],
);
const SLACK_SEND_OUTPUT = withBriefKeys(jsonObject(['ts', 'channel']), []);
const SLACK_SEND_TEMPLATE_FIELDS = ['message', 'threadTs'] as const;
const MCP_TOOL_CALL_OUTPUT = withItemShapes(
  withJsonKeys(
    withBriefKeys(
      jsonArray([MCP_STRUCTURED_OUTPUT_KEY], [MCP_STRUCTURED_OUTPUT_KEY]),
      [],
    ),
    [MCP_STRUCTURED_OUTPUT_KEY],
  ),
  {
    [BARE_OUTPUT_LIST]: UNKNOWN_ITEMS,
    [MCP_STRUCTURED_OUTPUT_KEY]: UNKNOWN_ITEMS,
  },
);
const PEOPLE_ITEM_SHAPES = {
  [BARE_OUTPUT_LIST]: objectItems(PERSON_FIELDS),
};
const TEAM_PEOPLE_ITEM_SHAPES = {
  [BARE_OUTPUT_LIST]: objectItems(PERSON_FIELDS, ['ownerUserId']),
};
const BARE_ID_LIST = { [BARE_OUTPUT_LIST]: SCALAR_ITEMS };

/**
 * Single declaration of what each step writes and which config fields resolve
 * variables. Recorded from the strategies themselves; the coverage tests in
 * apps/api/.../utils/stepContracts run the real strategies against it.
 */
export const STEP_CONTRACTS = {
  [ActionType.ARCHIVE]: action(NO_OUTPUT, {}, { requiresTriggerEmail: true }),
  [ActionType.ADD_LABELS]: action(
    NO_OUTPUT,
    {},
    { requiresTriggerEmail: true },
  ),
  [ActionType.REMOVE_LABELS]: action(
    NO_OUTPUT,
    {},
    { requiresTriggerEmail: true },
  ),
  [ActionType.FORWARD]: action(
    NO_OUTPUT,
    { literalFields: ['recipients[]'] },
    { requiresTriggerEmail: true },
  ),
  [ActionType.DELETE]: action(NO_OUTPUT, {}, { requiresTriggerEmail: true }),
  [ActionType.MARK_READ]: action(NO_OUTPUT, {}, { requiresTriggerEmail: true }),
  [ActionType.MARK_UNREAD]: action(
    NO_OUTPUT,
    {},
    { requiresTriggerEmail: true },
  ),
  [ActionType.NOTIFY_BY_EMAIL]: action(
    NO_OUTPUT,
    {},
    { requiresTriggerEmail: true },
  ),
  [ActionType.CHECK_IF]: {
    kind: StepKind.CONTAINER,
    // Plain text: "Condition met — ran N action(s). ..." (nothing is written when conditions[] is empty).
    output: { kind: StepOutputKind.SCALAR },
    templateFields: ['conditions[].value'],
    barePathFields: ['conditions[].variable'],
    childOutputsAddressable: true,
    runtimeSupported: true,
    webSupported: true,
    requiresTriggerEmail: false,
    pausesFlow: false,
    mailboxField: undefined,
    readsTrigger: false,
    idempotent: true,
  },
  [ActionType.AI_ASK]: action(
    {
      kind: StepOutputKind.SCHEMA_DEFINED,
      outputSchemaField: 'outputSchema',
    },
    {
      templateFields: ['prompt'],
      literalFields: ['systemPrompt'],
      stringOnlyFields: ['systemPrompt'],
    },
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.SUMMARIZE]: action(
    jsonObject(['summary', 'hasSummary']),
    {
      templateFields: ['prompt'],
      literalFields: ['systemPrompt'],
      stringOnlyFields: ['systemPrompt'],
    },
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.DECIDE]: action(
    { kind: StepOutputKind.BOOLEAN_STRING },
    {
      templateFields: ['prompt'],
      literalFields: ['systemPrompt'],
      stringOnlyFields: ['systemPrompt'],
    },
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.CHANGE_CATEGORY]: action(
    NO_OUTPUT,
    {},
    { requiresTriggerEmail: true },
  ),
  [ActionType.DRAFT_EMAIL]: action(
    DRAFT_OUTPUT,
    {
      templateFields: ['subject', 'body'],
      barePathFields: ['recipient'],
    },
    { mailboxField: MailboxConfigField.SENDER },
  ),
  [ActionType.DRAFT_REPLY]: action(
    DRAFT_OUTPUT,
    { templateFields: ['body'] },
    { requiresTriggerEmail: true },
  ),
  [ActionType.SEND_REPLY]: action(
    SEND_EMAIL_OUTPUT,
    { templateFields: ['body'] },
    { requiresTriggerEmail: true },
  ),
  [ActionType.GENERATE_DOBOX]: action(
    withItemShapes(jsonArray(), BARE_ID_LIST),
    {
      templateFields: ['prompt'],
    },
  ),
  [ActionType.LOAD_DOBOX]: action(
    withItemShapes(jsonArray(), {
      [BARE_OUTPUT_LIST]: objectItems(DOBOX_FIELDS),
    }),
    {},
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.LOAD_COMMUNICATIONS]: action(
    withItemShapes(jsonArray(), {
      [BARE_OUTPUT_LIST]: objectItems(COMMUNICATION_FIELDS),
    }),
    {},
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.SEND_NOTIFICATION_EMAIL]: action(NO_OUTPUT, {
    templateFields: ['heading', 'subject', 'description'],
  }),
  [ActionType.SEND_EMAIL]: action(
    SEND_EMAIL_OUTPUT,
    {
      templateFields: ['subject', 'body'],
      barePathFields: ['recipients[]'],
    },
    { mailboxField: MailboxConfigField.SENDER },
  ),
  [ActionType.SEND_USER_SLACK_MESSAGE]: action(
    SLACK_SEND_OUTPUT,
    {
      templateFields: SLACK_SEND_TEMPLATE_FIELDS,
    },
    { readsTrigger: false },
  ),
  [ActionType.SEND_BOT_SLACK_MESSAGE]: action(
    SLACK_SEND_OUTPUT,
    {
      templateFields: SLACK_SEND_TEMPLATE_FIELDS,
    },
    { readsTrigger: false },
  ),
  [ActionType.VALIDATE_DOBOX_RULES]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['PROCEED', 'BLOCK'],
      extraContextKeys: ['rules', 'matchType', 'matchedRuleId', 'reason'],
      conditionalContextKeys: ['matchType', 'matchedRuleId', 'reason'],
    },
    { templateFields: ['communicationsInput'] },
    { idempotent: true },
  ),
  [ActionType.APPEND_TO_DOBOX]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['SUCCESS', 'FAILED'],
      extraContextKeys: ['appendedEntryId', 'reason'],
    },
    {
      templateFields: ['inboxEntryId', 'communicationIds'],
      stringOnlyFields: ['communicationIds'],
    },
    { readsTrigger: false },
  ),
  [ActionType.CHECK_AND_APPEND_DOBOX]: action(
    withItemShapes(
      {
        kind: StepOutputKind.SCALAR,
        values: ['MATCHED', 'NO_MATCH'],
        extraContextKeys: [
          'matchedEntryId',
          'reason',
          'matchedCount',
          'matchedEntryIds',
        ],
        conditionalContextKeys: ['matchedCount', 'matchedEntryIds'],
      },
      { matchedEntryIds: SCALAR_ITEMS },
    ),
  ),
  [ActionType.CREATE_DOBOX]: action(
    withItemShapes(
      {
        kind: StepOutputKind.SCALAR,
        // Generated flows read the new id as .output.inboxEntryId, so it is written beside the bare id.
        keys: ['inboxEntryId'],
        extraContextKeys: ['createdEntryId', 'ignoredParticipantIds'],
        conditionalContextKeys: ['ignoredParticipantIds'],
      },
      { ignoredParticipantIds: SCALAR_ITEMS },
    ),
    {
      templateFields: ['subject', 'description', 'taskGroupId'],
      // Linked as message ids as typed; the store aborts the create when one does not exist.
      literalFields: ['communicationIds[]'],
    },
  ),
  [ActionType.RESOLVE_DOBOX]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['resolvedEntryId'] },
    {
      templateFields: ['inboxEntryId'],
    },
    { readsTrigger: false },
  ),
  [ActionType.ACCEPT_DOBOX_SUGGESTION]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['acceptedEntryId'] },
    { templateFields: ['inboxEntryId'] },
    { readsTrigger: false },
  ),
  [ActionType.ADD_DOBOX_COMMENT]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['commentedEntryId'] },
    {
      templateFields: ['inboxEntryId', 'comment'],
    },
    { readsTrigger: false },
  ),
  [ActionType.UPDATE_DOBOX_PARTICIPANTS]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['updatedEntryId'] },
    { templateFields: ['inboxEntryId'] },
    { readsTrigger: false },
  ),
  [ActionType.UPDATE_DOBOX_LABELS]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['updatedEntryId'] },
    {
      templateFields: ['inboxEntryId'],
    },
    { readsTrigger: false },
  ),
  [ActionType.PROCESS_DOBOX]: action(
    withItemShapes(
      jsonObject([
        'created',
        'appended',
        'resolved',
        'reopened',
        'looksResolved',
        'looksResolvedWithdrawn',
      ]),
      {
        created: SCALAR_ITEMS,
        appended: SCALAR_ITEMS,
        resolved: SCALAR_ITEMS,
        reopened: SCALAR_ITEMS,
        looksResolved: SCALAR_ITEMS,
        looksResolvedWithdrawn: SCALAR_ITEMS,
      },
    ),
  ),
  [ActionType.FOR_EACH]: {
    kind: StepKind.CONTAINER,
    output: withItemShapes(
      withJsonKeys(
        jsonObject(
          ['iterations', FOR_EACH_RESULTS_KEY],
          [FOR_EACH_RESULTS_KEY],
        ),
        [FOR_EACH_RESULTS_KEY],
      ),
      FOR_EACH_RESULT_ITEM_SHAPES,
    ),
    // A bare path here is NOT resolved: it stays literal and is split on commas.
    templateFields: ['sourceArray'],
    barePathFields: [],
    childOutputsAddressable: false,
    childOutputsReadableWithinIteration: true,
    childScopeVariables: [
      FOR_EACH_ITEM_VARIABLE,
      FOR_EACH_INDEX_VARIABLE,
      FOR_EACH_TOTAL_VARIABLE,
    ],
    runtimeSupported: true,
    webSupported: true,
    requiresTriggerEmail: false,
    pausesFlow: false,
    mailboxField: undefined,
    readsTrigger: false,
    idempotent: true,
  },
  [ActionType.SEND_USER_GOOGLE_CHAT_MESSAGE]: action(
    withBriefKeys(jsonObject(['messageName', 'threadName']), []),
    {
      templateFields: ['message', 'spaceName', 'threadName'],
    },
    { readsTrigger: false },
  ),
  [ActionType.MCP_TOOL_CALL]: action(MCP_TOOL_CALL_OUTPUT, {
    templateFields: ['arguments.*'],
  }),
  [ActionType.CALENDAR_RSVP]: action(
    jsonObject([...CALENDAR_RESULT_KEYS, 'responseStatus']),
    { templateFields: ['responseStatus'], barePathFields: ['eventId'] },
    { mailboxField: MailboxConfigField.CALENDAR },
  ),
  [ActionType.CALENDAR_CREATE_EVENT]: action(
    jsonObject([...CALENDAR_RESULT_KEYS, 'meetLink'], ['meetLink']),
    {
      templateFields: [
        'summary',
        'description',
        'startDateTime',
        'endDateTime',
        'location',
      ],
      barePathFields: ['attendees[]'],
    },
    { mailboxField: MailboxConfigField.CALENDAR },
  ),
  [ActionType.CALENDAR_DELETE_EVENT]: action(
    jsonObject(CALENDAR_RESULT_KEYS),
    { barePathFields: ['eventId'] },
    { mailboxField: MailboxConfigField.CALENDAR },
  ),
  [ActionType.CALENDAR_RESCHEDULE_EVENT]: action(
    jsonObject(CALENDAR_RESULT_KEYS),
    {
      templateFields: ['startDateTime', 'endDateTime'],
      barePathFields: ['eventId'],
    },
    { mailboxField: MailboxConfigField.CALENDAR },
  ),
  [ActionType.CALENDAR_ADD_ATTENDEES]: action(
    jsonObject(CALENDAR_RESULT_KEYS),
    { barePathFields: ['eventId', 'attendees[]'] },
    { mailboxField: MailboxConfigField.CALENDAR },
  ),
  [ActionType.CALENDAR_REMOVE_ATTENDEES]: action(
    jsonObject(CALENDAR_RESULT_KEYS),
    { barePathFields: ['eventId', 'attendees[]'] },
    { mailboxField: MailboxConfigField.CALENDAR },
  ),
  [ActionType.AGENT_TASK]: action(
    {
      kind: StepOutputKind.SCHEMA_DEFINED,
      outputSchemaField: 'outputSchema',
    },
    {
      templateFields: ['goal'],
      literalFields: ['systemPrompt'],
      stringOnlyFields: ['systemPrompt'],
    },
  ),
  [ActionType.LOAD_MEETINGS]: action(
    withItemShapes(jsonArray(), {
      [BARE_OUTPUT_LIST]: objectItems(MEETING_FIELDS),
    }),
    {},
    { mailboxField: MailboxConfigField.CALENDAR, idempotent: true },
  ),
  [ActionType.SEND_INSTAGRAM_MESSAGE]: action(
    jsonObject(['messageId', 'sentAt']),
    { templateFields: ['igAccountId', 'conversationId', 'message'] },
  ),
  [ActionType.REPLY_INSTAGRAM_COMMENT]: action(
    jsonObject(['commentId', 'repliedAt']),
    { templateFields: ['igAccountId', 'commentId', 'message'] },
  ),
  [ActionType.PUBLISH_INSTAGRAM_POST]: action(
    jsonObject(['mediaId', 'permalink', 'publishedAt']),
    {
      templateFields: [
        'igAccountId',
        'kind',
        'caption',
        'imageUrl',
        'generatedImageId',
        'videoUrl',
        'coverUrl',
        'children[].imageUrl',
        'children[].generatedImageId',
      ],
    },
  ),
  [ActionType.SEND_WHATSAPP_MESSAGE]: action(
    jsonObject(
      ['success', 'wamid', 'sentAt', 'reason', 'error'],
      ['wamid', 'sentAt', 'reason', 'error'],
    ),
    {
      templateFields: [
        'phoneNumberId',
        'to',
        'message',
        'replyToMessageId',
        'templateBodyParameters[]',
      ],
    },
    { readsTrigger: false },
  ),
  [ActionType.NOTIFY_ASSISTANT]: action(
    NO_OUTPUT,
    {
      templateFields: ['messageTemplate'],
    },
    { readsTrigger: false },
  ),
  [ActionType.ASK_USER]: action(
    {
      kind: StepOutputKind.SCALAR,
      extraContextKeys: [ASK_USER_QUESTION_KEY],
      writtenOnPauseKeys: [ASK_USER_QUESTION_KEY],
      legacyKeys: [LEGACY_DECISION_KEY],
      writtenOnResume: true,
    },
    { templateFields: ['messageTemplate'] },
    { pausesFlow: true, readsTrigger: false },
  ),
  [ActionType.RUN_ASSISTANT]: action(
    NO_OUTPUT,
    {
      templateFields: ['promptTemplate'],
    },
    { readsTrigger: false },
  ),
  [ActionType.LOAD_TEAM_MEMBERS]: action(
    withItemShapes(listedIn(jsonObject(['members']), 'members'), {
      members: objectItems(TEAM_MEMBER_FIELDS),
    }),
    {
      templateFields: ['teamId'],
    },
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.CHECK_TEAM_CALENDAR]: action(
    withItemShapes(
      jsonObject(
        ['mode', 'slots', 'events', 'membersChecked', 'skipped', 'error'],
        ['slots', 'events', 'error'],
      ),
      {
        slots: objectItems([requiredField('start'), requiredField('end')]),
        events: objectItems([
          requiredField('memberName'),
          requiredField('title'),
          requiredField('start'),
          requiredField('end'),
          optionalField('location'),
        ]),
        membersChecked: objectItems([
          requiredField('name'),
          requiredField('email'),
        ]),
        skipped: objectItems([
          requiredField('member'),
          requiredField('reason'),
        ]),
      },
    ),
    {
      templateFields: [
        'members',
        'timeMin',
        'timeMax',
        'range',
        'subjectContains',
        'durationMinutes',
        'maxSlots',
        'includeSelf',
      ],
    },
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.LOAD_KNOWLEDGE_PAGE]: action(
    { kind: StepOutputKind.SCALAR },
    {},
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.APPROVAL_GATE]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['APPROVED', 'REJECTED'],
      legacyKeys: [LEGACY_DECISION_KEY],
      writtenOnResume: true,
    },
    { templateFields: ['title', 'summaryPrompt'] },
    { pausesFlow: true },
  ),
  [ActionType.CLASSIFY_ACTIONABILITY]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['ACTIONABLE', 'NOT_ACTIONABLE'],
      extraContextKeys: [
        'score',
        'reason',
        CLASSIFY_ACTIONABILITY_SOURCE_KIND_KEY,
      ],
      conditionalContextKeys: [CLASSIFY_ACTIONABILITY_SOURCE_KIND_KEY],
    },
    { templateFields: ['communicationsInput'] },
    { idempotent: true },
  ),
  [ActionType.SEARCH_PEOPLE]: action(
    withItemShapes(jsonArray(['count']), PEOPLE_ITEM_SHAPES),
    {
      templateFields: ['name', 'email', 'jobTitle', 'teamId'],
    },
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.UPDATE_PERSON_NOTES]: action(
    {
      kind: StepOutputKind.SCALAR,
      extraContextKeys: ['__updateFailed'],
      conditionalContextKeys: ['__updateFailed'],
    },
    { templateFields: ['personId', 'notesTemplate', 'teamId'] },
    { readsTrigger: false },
  ),
  [ActionType.LOAD_TEAM_PEOPLE]: action(
    withItemShapes(jsonArray(['count']), TEAM_PEOPLE_ITEM_SHAPES),
    {
      templateFields: ['teamId', 'query'],
    },
    { readsTrigger: false, idempotent: true },
  ),
  [ActionType.CREATE_PERSON_EVENT]: action(
    {
      kind: StepOutputKind.SCALAR,
      extraContextKeys: ['__updateFailed'],
      conditionalContextKeys: ['__updateFailed'],
    },
    {
      templateFields: [
        'personId',
        'personEmail',
        'titleTemplate',
        'descriptionTemplate',
        'teamId',
      ],
    },
  ),
  [ActionType.GBP_REPLY_REVIEW]: action(
    jsonObject(['success', 'reviewName', 'isUpdate']),
    { templateFields: ['reviewName', 'replyText'] },
  ),
  [ActionType.PUBLISH_FACEBOOK_POST]: action(
    jsonObject(['postId', 'permalink', 'scheduled', 'pageId'], ['permalink']),
    {
      templateFields: ['pageId', 'scheduledAt', 'message', 'imageUrl', 'link'],
    },
  ),
  [ActionType.REPLY_FACEBOOK_COMMENT]: action(
    jsonObject(['replyId', 'commentId', 'pageId', 'private']),
    { templateFields: ['message', 'commentId', 'pageId'] },
  ),
  [ActionType.LOAD_FACEBOOK_POSTS]: action(
    withItemShapes(
      listedIn(
        jsonObject(['posts', 'nextCursor', 'pageId'], ['nextCursor']),
        'posts',
      ),
      { posts: objectItems(FACEBOOK_POST_FIELDS) },
    ),
    { templateFields: ['pageId'] },
    { idempotent: true },
  ),
  [ActionType.LOAD_FACEBOOK_COMMENTS]: action(
    withItemShapes(
      listedIn(
        jsonObject(
          ['comments', 'nextCursor', 'postId', 'pageId'],
          ['nextCursor'],
        ),
        'comments',
      ),
      { comments: objectItems(FACEBOOK_COMMENT_FIELDS) },
    ),
    { templateFields: ['postId', 'pageId'] },
    { idempotent: true },
  ),
  [ActionType.SEND_FACEBOOK_MESSAGE]: action(
    jsonObject(
      ['messageId', 'recipientId', 'pageId', 'refused', 'reason', 'message'],
      ['messageId', 'refused', 'reason', 'message'],
    ),
    { templateFields: ['message', 'recipientId', 'pageId'] },
  ),
  [ActionType.SEND_TEAMS_MESSAGE]: action(
    {
      kind: StepOutputKind.CONTEXT_KEYS,
      keys: ['messageId', 'webUrl'],
      optionalKeys: ['webUrl'],
    },
    { templateFields: ['chatId', 'message'] },
  ),
  [ActionType.LOAD_CHAT_COMMUNICATIONS]: action(
    withItemShapes(jsonArray(['count']), {
      [BARE_OUTPUT_LIST]: objectItems(
        CHAT_MESSAGE_FIELDS,
        CHAT_MESSAGE_HIDDEN_FIELDS,
      ),
    }),
    {
      // Which of these apply depends on config.platform.
      templateFields: [
        'channelId',
        'pageId',
        'conversationId',
        'igAccountId',
        'chatId',
        'emailAddress',
        'spaceName',
        'phoneNumberId',
      ],
    },
  ),
} satisfies Record<ActionType, StepContract>;

type ContractOf<Type extends ActionType> = (typeof STEP_CONTRACTS)[Type];

type ActionTypesWhere<Match> = {
  [Type in ActionType]: ContractOf<Type> extends Match ? Type : never;
}[ActionType];

/** Has a strategy in `strategiesMap`; containers (CHECK_IF, FOR_EACH) are dispatched outside it. */
export type RuntimeSupportedActionType = ActionTypesWhere<{
  kind: StepKind.ACTION;
  runtimeSupported: true;
}>;

/** Safe for the flow generator to emit: runs in the runtime, and opens in the web editor. */
export type GeneratableActionType = ActionTypesWhere<{
  runtimeSupported: true;
  webSupported: true;
}>;

export const GENERATABLE_ACTION_TYPES: ReadonlySet<ActionType> = new Set(
  (Object.keys(STEP_CONTRACTS) as ActionType[]).filter(
    (type) =>
      STEP_CONTRACTS[type].runtimeSupported &&
      STEP_CONTRACTS[type].webSupported,
  ),
);

export type TriggerEmailRequiredActionType = ActionTypesWhere<{
  requiresTriggerEmail: true;
}>;

export type PausingActionType = ActionTypesWhere<{ pausesFlow: true }>;

export const PAUSING_ACTION_TYPES: ReadonlySet<ActionType> = new Set(
  (Object.keys(STEP_CONTRACTS) as ActionType[]).filter(
    (type) => STEP_CONTRACTS[type].pausesFlow,
  ),
);

export const TRIGGER_EMAIL_REQUIRED_ACTION_TYPES: ReadonlySet<ActionType> =
  new Set(
    (Object.keys(STEP_CONTRACTS) as ActionType[]).filter(
      (type) => STEP_CONTRACTS[type].requiresTriggerEmail,
    ),
  );

export const stepMailboxField = (
  stepType: ActionType | string,
): MailboxConfigField | undefined =>
  (STEP_CONTRACTS as Partial<Record<string, StepContract>>)[stepType]
    ?.mailboxField;

interface StepTreeNode {
  stepType: string;
  childSteps?: readonly StepTreeNode[];
}

const contractOf = (stepType: string): StepContract | undefined =>
  (STEP_CONTRACTS as Partial<Record<string, StepContract>>)[stepType];

/** True for a type outside the table: a step the contract does not know is assumed to read the trigger. */
export const stepReadsTrigger = (stepType: string): boolean =>
  contractOf(stepType)?.readsTrigger ?? true;

/** True when the step or any descendant reads the trigger. */
export const stepTreeReadsTrigger = (step: StepTreeNode): boolean =>
  stepReadsTrigger(step.stepType) ||
  (step.childSteps ?? []).some(stepTreeReadsTrigger);

/** False for a type outside the table: a step the contract does not know is assumed to have effects. */
export const stepIsIdempotent = (stepType: string): boolean =>
  contractOf(stepType)?.idempotent ?? false;

/** True only when the step and every descendant are idempotent. */
export const stepTreeIsIdempotent = (step: StepTreeNode): boolean =>
  stepIsIdempotent(step.stepType) &&
  (step.childSteps ?? []).every(stepTreeIsIdempotent);

export const isEmailTrigger = (triggerType: TriggerType | undefined): boolean =>
  triggerType === TriggerType.EMAIL;

export const triggerProvidesMailbox = (
  triggerType: TriggerType | undefined,
): boolean => isEmailTrigger(triggerType);

type OutputOf<Type extends ActionType> = ContractOf<Type>['output'];

/** Runtime-supported steps, containers included, that write `.output` (or `.output.<key>` entries) on success. */
export type OutputWritingActionType = {
  [Type in ActionTypesWhere<{
    runtimeSupported: true;
  }>]: OutputOf<Type> extends {
    kind: StepOutputKind.NONE;
  }
    ? never
    : Type;
}[ActionTypesWhere<{ runtimeSupported: true }>];

/** The `.output.<key>` context entries a step declares; `never` when it declares none. */
export type StepOutputKey<Type extends ActionType> =
  OutputOf<Type> extends {
    keys: readonly (infer Key extends string)[];
  }
    ? Key
    : never;

// Steps that reach someone or something outside the workspace; AGENT_TASK counts only when its toolSets include MCP (see the save rule).
export const EXTERNAL_EFFECT_ACTION_TYPES: ReadonlySet<ActionType> = new Set([
  ActionType.MCP_TOOL_CALL,
  ActionType.SEND_EMAIL,
  ActionType.SEND_REPLY,
  ActionType.FORWARD,
  ActionType.SEND_NOTIFICATION_EMAIL,
  ActionType.SEND_USER_SLACK_MESSAGE,
  ActionType.SEND_BOT_SLACK_MESSAGE,
  ActionType.SEND_USER_GOOGLE_CHAT_MESSAGE,
  ActionType.SEND_TEAMS_MESSAGE,
  ActionType.SEND_WHATSAPP_MESSAGE,
  ActionType.SEND_INSTAGRAM_MESSAGE,
  ActionType.REPLY_INSTAGRAM_COMMENT,
  ActionType.PUBLISH_INSTAGRAM_POST,
  ActionType.SEND_FACEBOOK_MESSAGE,
  ActionType.REPLY_FACEBOOK_COMMENT,
  ActionType.PUBLISH_FACEBOOK_POST,
  ActionType.GBP_REPLY_REVIEW,
  ActionType.CALENDAR_RSVP,
  ActionType.CALENDAR_CREATE_EVENT,
  ActionType.CALENDAR_RESCHEDULE_EVENT,
  ActionType.CALENDAR_DELETE_EVENT,
  ActionType.CALENDAR_ADD_ATTENDEES,
  ActionType.CALENDAR_REMOVE_ATTENDEES,
]);
